<script setup>
import { ref, computed } from 'vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'
import { FULFILMENT, orderFulfilmentPct } from '@/data/shared/orderModel.js'

const tab = ref('queue')
const search = ref('')
const drawerOrderNo = ref(null)
const commentText = ref(''); const cancelReason = ref('')
const fulfilInputs = ref({})
const flash = ref(null)
const notify = m => { flash.value = m; setTimeout(() => { flash.value = null }, 2600) }

const orders = computed(() => store.telecallerV2.orders.value)
const processedOrders = computed(() => [...store.telecallerV2.processedOrders.value].sort((a,b) => new Date(b.orderDate) - new Date(a.orderDate)))
const inventory = store.telecallerV2.inventory
const scmRequests = computed(() => store.telecallerV2.scmRequests.value)
const stockBySku = computed(() => Object.fromEntries(inventory.map(i => [i.productSku, i.availableQty])))

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return orders.value.filter(o => !q || o.orderNo.toLowerCase().includes(q) || o.pharmacyName.toLowerCase().includes(q))
})
const drawerOrder = computed(() => {
  if (!drawerOrderNo.value) return null
  return orders.value.find(o => o.orderNo === drawerOrderNo.value)
    || processedOrders.value.find(o => o.orderNo === drawerOrderNo.value)
    || null
})
const isActionable = computed(() => drawerOrder.value && drawerOrder.value.currentStage === 'Telecaller Validation')

function openDrawer(o) {
  drawerOrderNo.value = o.orderNo
  commentText.value = ''; cancelReason.value = ''
  // Default each line to whatever live stock can actually cover — the
  // telecaller adjusts down, rather than typing every number from scratch.
  const seed = {}
  o.lineItems.forEach(li => {
    seed[li.productSku] = li.fulfilledQty !== null && li.fulfilledQty !== undefined
      ? li.fulfilledQty
      : Math.min(li.quantity, stockBySku.value[li.productSku] ?? 0)
  })
  fulfilInputs.value = seed
}
function applyFulfilment(li) {
  if (!drawerOrderNo.value) return
  store.telecallerV2.setFulfilment(drawerOrderNo.value, li.productSku, fulfilInputs.value[li.productSku])
  notify(`${li.productName} marked`)
}
function shortfall(li) {
  const avail = stockBySku.value[li.productSku] ?? 0
  return Math.max(0, li.quantity - avail)
}
function raiseScmFor(li) {
  if (!drawerOrder.value) return
  const req = store.telecallerV2.raiseScmRequest({
    productSku: li.productSku, orderNo: drawerOrder.value.orderNo,
    requestedQty: shortfall(li) || li.quantity,
    note: `Raised from ${drawerOrder.value.orderNo} — live stock ${stockBySku.value[li.productSku] ?? 0} against ${li.quantity} ordered.`,
  })
  if (req) notify(`${req.requestId} raised for ${li.productName}`)
}
function confirm() {
  if (!drawerOrderNo.value) return
  // Auto-apply the current input for any line that was never explicitly
  // "Marked" — clicking Confirm without marking every line first left
  // fulfilledQty null, which is exactly why Warehouse showed "—" for
  // Telecaller Approved even though a quantity was sitting in the input.
  drawerOrder.value?.lineItems.forEach(li => {
    if (li.fulfilmentStatus === FULFILMENT.PENDING) {
      store.telecallerV2.setFulfilment(drawerOrderNo.value, li.productSku, fulfilInputs.value[li.productSku])
    }
  })
  store.telecallerV2.confirmOrder(drawerOrderNo.value)
  drawerOrderNo.value = null
}
function cancel() { if (drawerOrderNo.value) { store.telecallerV2.cancelOrder(drawerOrderNo.value, cancelReason.value.trim()); drawerOrderNo.value = null } }
function comment() { if (drawerOrderNo.value && commentText.value.trim()) { store.telecallerV2.addComment(drawerOrderNo.value, commentText.value.trim()); drawerOrderNo.value = null } }

function toneFor(status) {
  if (status === FULFILMENT.FULL) return 'green'
  if (status === FULFILMENT.PARTIAL) return 'yellow'
  if (status === FULFILMENT.UNAVAILABLE) return 'red'
  return 'yellow'
}
// Order-level %, quantity-weighted, based on what THIS telecaller fulfilled.
function fulfilPct(o) { return orderFulfilmentPct(o, 'fulfilledQty') }

// Standalone SCM request form (point 6) — not every request comes off an order.
const showScmForm = ref(false)
const scmForm = ref({ productSku: '', requestedQty: 50, note: '' })
function submitScm() {
  if (!scmForm.value.productSku || !scmForm.value.requestedQty) return
  const req = store.telecallerV2.raiseScmRequest({ ...scmForm.value })
  if (req) { notify(`${req.requestId} raised`); showScmForm.value = false; scmForm.value = { productSku: '', requestedQty: 50, note: '' } }
}
</script>

<template>
  <div>
    <div class="card module-header" style="display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap;">
      <h2>{{ orders.length }} orders pending validation</h2>
      <div style="display:flex;gap:4px;">
        <button class="filter-select" :style="tab==='queue'?'background:var(--color-primary-dark);color:#fff;border-color:var(--color-primary-dark);':'cursor:pointer;'" @click="tab='queue'">Validation Queue</button>
        <button class="filter-select" :style="tab==='inventory'?'background:var(--color-primary-dark);color:#fff;border-color:var(--color-primary-dark);':'cursor:pointer;'" @click="tab='inventory'">Live Inventory</button>
        <button class="filter-select" :style="tab==='scm'?'background:var(--color-primary-dark);color:#fff;border-color:var(--color-primary-dark);':'cursor:pointer;'" @click="tab='scm'">SCM Requests ({{ scmRequests.length }})</button>
        <button class="filter-select" :style="tab==='processed'?'background:var(--color-primary-dark);color:#fff;border-color:var(--color-primary-dark);':'cursor:pointer;'" @click="tab='processed'">Processed ({{ processedOrders.length }})</button>
      </div>
    </div>

    <div v-if="flash" style="position:fixed;bottom:24px;right:24px;background:var(--color-primary-dark);color:#fff;padding:10px 16px;border-radius:8px;font-size:13px;box-shadow:var(--shadow-lg);z-index:120;">{{ flash }}</div>

    <!-- QUEUE -->
    <div v-if="tab==='queue'" class="card">
      <div class="toolbar">
        <input class="search-input" v-model="search" placeholder="Search order no. or pharmacy…" />
        <span class="result-count">{{ filtered.length }} of {{ orders.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Order No.</th><th>Pharmacy</th><th>Products</th><th>Value</th><th>Approved By</th></tr></thead>
          <tbody>
            <tr v-for="o in filtered" :key="o.orderNo" class="clickable" @click="openDrawer(o)">
              <td class="cell-strong">{{ o.orderNo }}</td><td>{{ o.pharmacyName }}</td>
              <td>{{ o.lineItems[0].productName }}<span v-if="o.lineItems.length>1" class="cell-muted"> +{{ o.lineItems.length-1 }} more</span></td>
              <td class="cell-muted">${{ o.orderValue.toLocaleString() }}</td>
              <td class="cell-muted">{{ o.approvedBy || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- LIVE INVENTORY -->
    <div v-if="tab==='inventory'" class="card">
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Product</th><th>SKU</th><th>Warehouse</th><th>Available</th></tr></thead>
          <tbody>
            <tr v-for="i in inventory" :key="i.productSku">
              <td class="cell-strong">{{ i.productName }}</td><td class="cell-muted">{{ i.productSku }}</td>
              <td class="cell-muted">{{ i.warehouse }}</td>
              <td :style="i.availableQty < 50 ? 'color:var(--status-red);font-weight:700;' : ''">{{ i.availableQty }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- SCM REQUESTS -->
    <div v-if="tab==='scm'">
      <div style="margin-bottom:12px;"><button class="btn-primary" @click="showScmForm=true">+ Raise SCM Request</button></div>
      <div class="card">
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Request ID</th><th>Product</th><th>Order No.</th><th>Qty</th><th>Raised On</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="r in scmRequests" :key="r.requestId">
                <td class="cell-strong">{{ r.requestId }}</td><td>{{ r.productName }}</td>
                <td class="cell-muted">{{ r.orderNo || '—' }}</td><td class="cell-muted">{{ r.requestedQty }}</td>
                <td class="cell-muted">{{ r.raisedOn }}</td>
                <td><span class="status-pill yellow"><span class="dot"></span>{{ r.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- PROCESSED -->
    <div v-if="tab==='processed'" class="card">
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Order No.</th><th>Pharmacy</th><th>Value</th><th>Current Stage</th><th>Fulfilment</th></tr></thead>
          <tbody>
            <tr v-for="o in processedOrders" :key="o.orderNo" class="clickable" @click="openDrawer(o)">
              <td class="cell-strong">{{ o.orderNo }}</td><td>{{ o.pharmacyName }}</td>
              <td class="cell-muted">${{ o.orderValue.toLocaleString() }}</td>
              <td class="cell-muted">{{ o.currentStage }}</td>
              <td>
                <span v-if="fulfilPct(o) !== null" class="status-pill" :class="fulfilPct(o)===100?'green':'yellow'"><span class="dot"></span>{{ fulfilPct(o) }}% fulfilled</span>
                <span v-else class="cell-muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ORDER DRAWER -->
    <DetailDrawer :open="!!drawerOrder" :title="drawerOrder?.orderNo || ''" wide @close="drawerOrderNo=null">
      <template v-if="drawerOrder">
        <div class="oos-meta-grid" style="margin-bottom:18px;">
          <div><div class="label">Pharmacy</div>{{ drawerOrder.pharmacyName }}</div>
          <div><div class="label">Order Date</div>{{ drawerOrder.orderDate }}</div>
          <div><div class="label">Approved By</div>{{ drawerOrder.approvedBy || '—' }}</div>
        </div>

        <div v-if="fulfilPct(drawerOrder) !== null" class="oos-remarks" style="margin-bottom:16px;">
          <strong style="display:block;font-size:11px;text-transform:uppercase;color:var(--color-text-muted);">Order Fulfilment</strong>
          {{ fulfilPct(drawerOrder) }}% fulfilled
        </div>

        <div class="eyebrow" style="margin-bottom:8px;">Fulfilment {{ isActionable ? 'against live stock' : '(as sent to Warehouse)' }}</div>
        <table class="data-table" style="margin-bottom:18px;">
          <thead><tr><th>Product</th><th>Ordered</th><th>Free</th><th v-if="isActionable">In Stock</th><th>Fulfil Qty</th><th>Status</th><th v-if="isActionable"></th></tr></thead>
          <tbody>
            <tr v-for="li in drawerOrder.lineItems" :key="li.productSku">
              <td class="cell-strong">{{ li.productName }} <span v-if="li.schemeApplied" class="tier-badge Gold">10+1</span></td>
              <td class="cell-muted">{{ li.quantity }}</td>
              <td class="cell-muted">{{ li.freeQuantity || 0 }}</td>
              <td v-if="isActionable" :style="(stockBySku[li.productSku] ?? 0) < li.quantity ? 'color:var(--status-red);font-weight:700;' : ''">{{ stockBySku[li.productSku] ?? 0 }}</td>
              <td v-if="isActionable"><input class="form-input" type="number" min="0" :max="li.quantity" style="width:80px;padding:4px 8px;" v-model.number="fulfilInputs[li.productSku]" /></td>
              <td v-else class="cell-muted">{{ li.fulfilledQty ?? '—' }}</td>
              <td><span class="status-pill" :class="toneFor(li.fulfilmentStatus)"><span class="dot"></span>{{ li.fulfilmentStatus }}</span></td>
              <td v-if="isActionable" style="white-space:nowrap;">
                <button class="filter-select" style="cursor:pointer;font-size:10.5px;" @click="applyFulfilment(li)">Mark</button>
                <button v-if="shortfall(li) > 0" class="filter-select" style="cursor:pointer;font-size:10.5px;margin-left:4px;" @click="raiseScmFor(li)">SCM</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="isActionable" style="border-top:1px solid var(--color-border);padding-top:16px;">
          <button class="btn-primary" style="margin-right:8px;" @click="confirm">Confirm &amp; Send to Warehouse</button>
          <div style="display:flex;gap:8px;margin:10px 0;">
            <input class="form-input" v-model="cancelReason" placeholder="Cancel reason" />
            <button class="filter-select" style="cursor:pointer;color:var(--status-red);" @click="cancel">Cancel</button>
          </div>
          <div style="display:flex;gap:8px;">
            <input class="form-input" v-model="commentText" placeholder="Comment back to MR…" />
            <button class="filter-select" style="cursor:pointer;" @click="comment">Comment</button>
          </div>
        </div>
        <p v-else class="cell-muted" style="font-size:12.5px;">Already sent on — currently at {{ drawerOrder.currentStage }}.</p>
      </template>
    </DetailDrawer>

    <!-- SCM REQUEST FORM -->
    <DetailDrawer :open="showScmForm" title="Raise SCM Request" @close="showScmForm=false">
      <div class="form-group">
        <label>Product</label>
        <select class="form-select" v-model="scmForm.productSku">
          <option value="" disabled>Select…</option>
          <option v-for="i in inventory" :key="i.productSku" :value="i.productSku">{{ i.productName }} (in stock: {{ i.availableQty }})</option>
        </select>
      </div>
      <div class="form-group"><label>Requested Qty</label><input class="form-input" type="number" min="1" v-model.number="scmForm.requestedQty" /></div>
      <div class="form-group"><label>Note</label><input class="form-input" v-model="scmForm.note" placeholder="Reason for the request" /></div>
      <button class="btn-primary" :disabled="!scmForm.productSku || !scmForm.requestedQty" @click="submitScm">Raise Request</button>
    </DetailDrawer>
  </div>
</template>
